// Static Site Deployer Page
import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { WorkspaceLayout } from '@/components/layout/WorkspaceLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Globe,
  Upload,
  GitBranch,
  Loader2,
  Check,
  X,
  ExternalLink,
  Copy,
  Trash2,
  RefreshCw,
  FileCode,
  FolderOpen,
  Lock,
  Eye,
  EyeOff,
  ChevronDown,
  Rocket,
  Package,
  Link2,
  AlertCircle,
  CheckCircle,
  Clock,
  FileText,
  Maximize2,
  Minimize2,
  BookOpen,
  Share2,
  Settings,
  Pencil,
  Search,
  Container
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/lib/auth-context';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

const API_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/^http:/, 'https:');

// Helper to safely parse JSON from API responses
// Prevents crash when backend returns HTML (nginx timeout, React fallback, etc.)
const safeParseJSON = async (response) => {
  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    return response.json();
  }
  // Non-JSON response - extract meaningful error text
  const text = await response.text();
  // If it's HTML, provide a user-friendly error message
  if (text.trim().startsWith('<!') || text.trim().startsWith('<html')) {
    if (response.status === 502 || response.status === 504) {
      throw new Error('Le serveur backend est injoignable. Vérifiez que le service est démarré.');
    }
    if (response.status === 408) {
      throw new Error('Le déploiement a pris trop de temps (timeout)');
    }
    throw new Error(`Erreur serveur (${response.status}). La réponse n'est pas au format JSON.`);
  }
  // Try to parse as JSON anyway (some servers don't set Content-Type correctly)
  try {
    return JSON.parse(text);
  } catch {
    throw new Error(text.substring(0, 300) || `Erreur serveur (${response.status})`);
  }
};

// Git Provider Icons
const GitProviderIcon = ({ provider, className = "h-4 w-4" }) => {
  switch (provider) {
    case 'github':
      return (
        <svg className={className} viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
        </svg>
      );
    case 'gitlab':
      return (
        <svg className={className} viewBox="0 0 24 24" fill="currentColor">
          <path d="M22.65 14.39L12 22.13 1.35 14.39a.84.84 0 0 1-.3-.94l1.22-3.78 2.44-7.51A.42.42 0 0 1 4.82 2a.43.43 0 0 1 .58 0 .42.42 0 0 1 .11.18l2.44 7.49h8.1l2.44-7.51A.42.42 0 0 1 18.6 2a.43.43 0 0 1 .58 0 .42.42 0 0 1 .11.18l2.44 7.51L23 13.45a.84.84 0 0 1-.35.94z"/>
        </svg>
      );
    case 'bitbucket':
      return (
        <svg className={className} viewBox="0 0 24 24" fill="currentColor">
          <path d="M.778 1.213a.768.768 0 0 0-.768.892l3.263 19.81c.084.5.515.868 1.022.873H19.95a.772.772 0 0 0 .77-.646l3.27-20.03a.768.768 0 0 0-.768-.891zM14.52 15.53H9.522L8.17 8.466h7.561z"/>
        </svg>
      );
    default:
      return <GitBranch className={className} />;
  }
};

// Detect Git provider from URL
const detectGitProvider = (url) => {
  if (!url) return null;
  const urlLower = url.toLowerCase();
  if (urlLower.includes('github.com')) return 'github';
  if (urlLower.includes('gitlab.com')) return 'gitlab';
  if (urlLower.includes('bitbucket.org')) return 'bitbucket';
  if (urlLower.includes('.git') || urlLower.includes('git')) return 'git';
  return null;
};

export default function StaticSiteDeployerPage() {
  const { workspaceId } = useParams();
  const navigate = useNavigate();
  const { user, canManage } = useAuth();
  const [sites, setSites] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deploying, setDeploying] = useState(false);
  const [deployStatus, setDeployStatus] = useState('');
  const [activeTab, setActiveTab] = useState('zip');
  const [selectedSite, setSelectedSite] = useState(null);
  const [previewSite, setPreviewSite] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [successModal, setSuccessModal] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [editModal, setEditModal] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', courseId: '' });
  const [saving, setSaving] = useState(false);

  // ZIP Upload State
  const [zipFile, setZipFile] = useState(null);
  const [zipSiteName, setZipSiteName] = useState('');
  const [zipCourseId, setZipCourseId] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  // Git Connections State
  const [gitConnections, setGitConnections] = useState([]);
  const [selectedConnection, setSelectedConnection] = useState(null);
  const [repos, setRepos] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loadingRepos, setLoadingRepos] = useState(false);
  const [loadingBranches, setLoadingBranches] = useState(false);
  const [repoSearchOpen, setRepoSearchOpen] = useState(false);
  const [repoSearch, setRepoSearch] = useState('');
  const [selectedRepo, setSelectedRepo] = useState(null);

  // Git Deploy State
  const [gitForm, setGitForm] = useState({
    repoUrl: '',
    branch: 'main',
    subfolder: '',
    siteName: '',
    username: '',
    token: '',
    isPrivate: false,
    courseId: ''
  });
  const [showToken, setShowToken] = useState(false);
  const [gitProvider, setGitProvider] = useState(null);

  // Dockerfile Deploy State
  const [dockerForm, setDockerForm] = useState({
    repoUrl: '',
    branch: 'main',
    dockerfile: 'Dockerfile',
    siteName: '',
    courseId: ''
  });
  const [dockerSelectedConnection, setDockerSelectedConnection] = useState(null);
  const [dockerSelectedRepo, setDockerSelectedRepo] = useState(null);
  const [dockerRepos, setDockerRepos] = useState([]);
  const [dockerBranches, setDockerBranches] = useState([]);
  const [dockerLoadingRepos, setDockerLoadingRepos] = useState(false);
  const [dockerLoadingBranches, setDockerLoadingBranches] = useState(false);
  const [dockerRepoSearchOpen, setDockerRepoSearchOpen] = useState(false);

  const token = localStorage.getItem('token');

  // Redirect non-admin users
  useEffect(() => {
    if (canManage === false) {
      navigate(`/workspace/${workspaceId}`);
    }
  }, [canManage, workspaceId, navigate]);

  useEffect(() => {
    loadSites();
    loadCourses();
    loadGitConnections();
  }, [workspaceId]);

  useEffect(() => {
    setGitProvider(detectGitProvider(gitForm.repoUrl));
  }, [gitForm.repoUrl]);

  // Load Git Connections
  const loadGitConnections = async () => {
    if (!user?.id) return;
    try {
      const response = await fetch(
        `${API_URL}/api/git-connections/?user_id=${user.id}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setGitConnections(data);
      }
    } catch (error) {
      console.error('Failed to load git connections:', error);
    }
  };

  // Load Repos from a Connection
  const loadRepos = async (connectionId, search = '') => {
    if (!connectionId || !user?.id) return;
    setLoadingRepos(true);
    try {
      const params = new URLSearchParams({ user_id: user.id });
      if (search) params.append('search', search);
      
      const response = await fetch(
        `${API_URL}/api/git-connections/${connectionId}/repos?${params}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setRepos(data);
      } else {
        console.error('Repos API error:', response.status);
        toast.error('Erreur lors du chargement des dépôts');
      }
    } catch (error) {
      console.error('Failed to load repos:', error);
      toast.error('Erreur lors du chargement des dépôts');
    } finally {
      setLoadingRepos(false);
    }
  };

  // Load Branches from a Repo
  const loadBranches = async (connectionId, repoId, repoFullName) => {
    if (!connectionId || !repoId || !user?.id) return;
    setLoadingBranches(true);
    try {
      const params = new URLSearchParams({ user_id: user.id });
      if (repoFullName) params.append('repo_full_name', repoFullName);
      
      const response = await fetch(
        `${API_URL}/api/git-connections/${connectionId}/repos/${repoId}/branches?${params}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setBranches(data);
        // Set default branch
        const defaultBranch = data.find(b => b.is_default);
        if (defaultBranch) {
          setGitForm(prev => ({ ...prev, branch: defaultBranch.name }));
        }
      }
    } catch (error) {
      console.error('Failed to load branches:', error);
    } finally {
      setLoadingBranches(false);
    }
  };

  // Handle Connection Selection
  const handleConnectionSelect = (connectionId) => {
    setSelectedConnection(connectionId);
    setSelectedRepo(null);
    setRepos([]);
    setBranches([]);
    setGitForm(prev => ({ ...prev, repoUrl: '', branch: 'main' }));
    
    if (connectionId) {
      loadRepos(connectionId);
    }
  };

  // Handle Repo Selection
  const handleRepoSelect = (repo) => {
    setSelectedRepo(repo);
    setGitForm(prev => ({
      ...prev,
      repoUrl: repo.clone_url,
      branch: repo.default_branch || 'main',
      siteName: prev.siteName || repo.name
    }));
    setRepoSearchOpen(false);
    
    // Load branches for this repo
    if (selectedConnection) {
      loadBranches(selectedConnection, repo.id, repo.full_name);
    }
  };

  const loadSites = async () => {
    try {
      const response = await fetch(`${API_URL}/api/sites`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSites(data.sites || []);
      }
    } catch (error) {
      console.error('Failed to load sites:', error);
      toast.error('Erreur lors du chargement des sites');
    } finally {
      setLoading(false);
    }
  };

  const loadCourses = async () => {
    if (!workspaceId) return;
    try {
      const response = await fetch(`${API_URL}/api/workspaces/${workspaceId}/courses`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCourses(data || []);
      }
    } catch (error) {
      console.error('Failed to load courses:', error);
    }
  };

  // ZIP Upload Handlers
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file && file.name.endsWith('.zip')) {
      setZipFile(file);
      setZipSiteName(file.name.replace('.zip', ''));
    } else {
      toast.error('Seuls les fichiers ZIP sont acceptés');
    }
  }, []);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file && file.name.endsWith('.zip')) {
      setZipFile(file);
      setZipSiteName(file.name.replace('.zip', ''));
    } else {
      toast.error('Seuls les fichiers ZIP sont acceptés');
    }
  };

  const deployZip = async () => {
    if (!zipFile) return;

    setDeploying(true);
    setDeployStatus('Téléchargement en cours...');
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('file', zipFile);
      formData.append('siteName', zipSiteName || zipFile.name.replace('.zip', ''));
      if (zipCourseId) {
        formData.append('courseId', zipCourseId);
        formData.append('workspaceId', workspaceId);
      }

      // Simulate upload progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 10, 90));
      }, 200);

      setDeployStatus('Extraction des fichiers...');
      
      const response = await fetch(`${API_URL}/api/sites/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      clearInterval(progressInterval);
      setUploadProgress(100);

      if (!response.ok) {
        const error = await safeParseJSON(response);
        throw new Error(error.detail || 'Upload failed');
      }

      const result = await safeParseJSON(response);
      setDeployStatus('Site déployé !');
      
      // Show success modal
      setSuccessModal(result);
      
      // Reset form
      setZipFile(null);
      setZipSiteName('');
      setZipCourseId('');
      setUploadProgress(0);
      
      // Reload sites
      loadSites();
      toast.success('Site déployé avec succès !');

    } catch (error) {
      toast.error(error.message || 'Échec du déploiement');
      setDeployStatus('');
    } finally {
      setDeploying(false);
    }
  };

  // Git Deploy Handlers
  const deployGit = async () => {
    if (!gitForm.repoUrl) {
      toast.error('URL du dépôt requise');
      return;
    }

    setDeploying(true);
    setDeployStatus('Connexion au dépôt...');

    try {
      setDeployStatus('Clonage du dépôt...');
      
      // Build deploy payload
      const deployPayload = {
        repoUrl: gitForm.repoUrl,
        branch: gitForm.branch || 'main',
        subfolder: gitForm.subfolder,
        siteName: gitForm.siteName,
        courseId: gitForm.courseId || null,
        workspaceId: gitForm.courseId ? workspaceId : null
      };

      // If using a configured Git connection, pass the connectionId
      if (selectedConnection && selectedRepo) {
        deployPayload.connectionId = selectedConnection;
      } else if (gitForm.isPrivate) {
        // Manual credentials for private repo
        deployPayload.username = gitForm.username;
        deployPayload.token = gitForm.token;
      }
      
      const response = await fetch(`${API_URL}/api/sites/deploy-git`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(deployPayload)
      });

      if (!response.ok) {
        const error = await safeParseJSON(response);
        throw new Error(error.detail || 'Clone failed');
      }

      const result = await safeParseJSON(response);
      setDeployStatus('Site publié !');
      
      // Show success modal
      setSuccessModal(result);
      
      // Reset form
      setGitForm({
        repoUrl: '',
        branch: 'main',
        subfolder: '',
        siteName: '',
        username: '',
        token: '',
        isPrivate: false,
        courseId: ''
      });
      
      // Reload sites
      loadSites();
      toast.success('Site déployé depuis Git !');

    } catch (error) {
      toast.error(error.message || 'Échec du clonage');
      setDeployStatus('');
    } finally {
      setDeploying(false);
    }
  };

  // Dockerfile Deploy Handlers
  const handleDockerConnectionSelect = (connectionId) => {
    setDockerSelectedConnection(connectionId);
    setDockerSelectedRepo(null);
    setDockerRepos([]);
    setDockerBranches([]);
    setDockerForm(prev => ({ ...prev, repoUrl: '', branch: 'main' }));
    if (connectionId) {
      loadDockerRepos(connectionId);
    }
  };

  const loadDockerRepos = async (connectionId, search = '') => {
    if (!connectionId || !user?.id) return;
    setDockerLoadingRepos(true);
    try {
      const params = new URLSearchParams({ user_id: user.id });
      if (search) params.append('search', search);
      const response = await fetch(
        `${API_URL}/api/git-connections/${connectionId}/repos?${params}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        setDockerRepos(await response.json());
      }
    } catch (error) {
      toast.error('Erreur chargement dépôts');
    } finally {
      setDockerLoadingRepos(false);
    }
  };

  const handleDockerRepoSelect = (repo) => {
    setDockerSelectedRepo(repo);
    setDockerForm(prev => ({
      ...prev,
      repoUrl: repo.clone_url,
      branch: repo.default_branch || 'main',
      siteName: prev.siteName || repo.name
    }));
    setDockerRepoSearchOpen(false);
    if (dockerSelectedConnection) {
      loadDockerBranches(dockerSelectedConnection, repo.id, repo.full_name);
    }
  };

  const loadDockerBranches = async (connectionId, repoId, repoFullName) => {
    if (!connectionId || !repoId || !user?.id) return;
    setDockerLoadingBranches(true);
    try {
      const params = new URLSearchParams({ user_id: user.id });
      if (repoFullName) params.append('repo_full_name', repoFullName);
      const response = await fetch(
        `${API_URL}/api/git-connections/${connectionId}/repos/${repoId}/branches?${params}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setDockerBranches(data);
        const def = data.find(b => b.is_default);
        if (def) setDockerForm(prev => ({ ...prev, branch: def.name }));
      }
    } catch (error) {
      console.error('Failed to load branches:', error);
    } finally {
      setDockerLoadingBranches(false);
    }
  };

  const deployDockerfile = async () => {
    if (!dockerForm.repoUrl) {
      toast.error('URL du dépôt requise');
      return;
    }
    if (!dockerForm.dockerfile) {
      toast.error('Chemin du Dockerfile requis');
      return;
    }

    setDeploying(true);
    setDeployStatus('Clonage et build Docker en cours...');

    try {
      const deployPayload = {
        repoUrl: dockerForm.repoUrl,
        branch: dockerForm.branch || 'main',
        subfolder: '',
        siteName: dockerForm.siteName,
        dockerfile: dockerForm.dockerfile,
        courseId: dockerForm.courseId || null,
        workspaceId: dockerForm.courseId ? workspaceId : null
      };

      if (dockerSelectedConnection && dockerSelectedRepo) {
        deployPayload.connectionId = dockerSelectedConnection;
      }

      const response = await fetch(`${API_URL}/api/sites/deploy-git`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(deployPayload)
      });

      if (!response.ok) {
        const error = await safeParseJSON(response);
        throw new Error(error.detail || 'Deploy failed');
      }

      const result = await safeParseJSON(response);
      setDeployStatus('Site déployé !');
      setSuccessModal(result);
      setDockerForm({
        repoUrl: '', branch: 'main', dockerfile: 'Dockerfile',
        siteName: '', courseId: ''
      });
      setDockerSelectedConnection(null);
      setDockerSelectedRepo(null);
      loadSites();
      toast.success('Site déployé via Dockerfile !');
    } catch (error) {
      toast.error(error.message || 'Échec du déploiement');
      setDeployStatus('');
    } finally {
      setDeploying(false);
    }
  };

  // Site Actions
  const redeploySite = async (site) => {
    if (site.source !== 'git') return;

    try {
      toast.info('Redéploiement en cours...');
      
      const response = await fetch(`${API_URL}/api/sites/${site.id}/redeploy`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Redeploy failed');
      }

      toast.success('Site redéployé avec succès !');
      loadSites();
    } catch (error) {
      toast.error(error.message || 'Échec du redéploiement');
    }
  };

  const deleteSite = async (site) => {
    try {
      const response = await fetch(`${API_URL}/api/sites/${site.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Delete failed');
      }

      toast.success('Site supprimé');
      setDeleteConfirm(null);
      if (previewSite?.id === site.id) {
        setPreviewSite(null);
      }
      loadSites();
    } catch (error) {
      toast.error('Échec de la suppression');
    }
  };

  const copyUrl = (site) => {
    const fullUrl = `${API_URL}/s/${site.slug || site.id}/`;
    navigator.clipboard.writeText(fullUrl);
    toast.success('URL copiée !');
  };

  const openSite = (site) => {
    window.open(`${API_URL}/s/${site.slug || site.id}/`, '_blank');
  };

  // Edit site handlers
  const openEditModal = (site) => {
    setEditForm({
      name: site.name || '',
      courseId: site.courseId || ''
    });
    setEditModal(site);
  };

  const saveEditSite = async () => {
    if (!editModal) return;
    
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/sites/${editModal.id}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: editForm.name,
          courseId: editForm.courseId || null,
          workspaceId: editForm.courseId ? workspaceId : null
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update site');
      }

      toast.success('Site mis à jour !');
      setEditModal(null);
      loadSites();
    } catch (error) {
      toast.error('Échec de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <WorkspaceLayout>
      <div className="page-container animate-fade-in">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900/30">
              <Globe className="h-6 w-6 text-purple-600" />
            </div>
            <h1 className="text-2xl font-bold">Static Site Deployer</h1>
            <Badge className="bg-red-500 text-white text-xs">NEW</Badge>
          </div>
          <p className="text-muted-foreground">
            Déployez vos sites HTML statiques en quelques secondes via ZIP ou Git
          </p>
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          {/* Deploy Panel */}
          <div className="space-y-6">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="zip" className="gap-2">
                  <Package className="h-4 w-4" />
                  Upload ZIP
                </TabsTrigger>
                <TabsTrigger value="git" className="gap-2">
                  <Link2 className="h-4 w-4" />
                  Deploy from Git
                </TabsTrigger>
                <TabsTrigger value="dockerfile" className="gap-2">
                  <Container className="h-4 w-4" />
                  Dockerfile
                </TabsTrigger>
              </TabsList>

              {/* ZIP Upload Tab */}
              <TabsContent value="zip" className="mt-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Upload className="h-5 w-5" />
                      Télécharger un fichier ZIP
                    </CardTitle>
                    <CardDescription>
                      Glissez-déposez votre archive ZIP contenant vos fichiers HTML
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Drop Zone */}
                    <div
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                      onClick={() => document.getElementById('zip-input').click()}
                      className={`
                        border-2 border-dashed rounded-lg p-8 text-center cursor-pointer
                        transition-all duration-200
                        ${isDragging 
                          ? 'border-purple-500 bg-purple-500/10' 
                          : 'border-muted-foreground/25 hover:border-purple-500/50 hover:bg-muted/50'
                        }
                        ${zipFile ? 'border-green-500 bg-green-500/10' : ''}
                      `}
                    >
                      <input
                        id="zip-input"
                        type="file"
                        accept=".zip"
                        className="hidden"
                        onChange={handleFileSelect}
                      />
                      {zipFile ? (
                        <div className="space-y-2">
                          <CheckCircle className="h-12 w-12 mx-auto text-green-500" />
                          <p className="font-medium">{zipFile.name}</p>
                          <p className="text-sm text-muted-foreground">
                            {(zipFile.size / (1024 * 1024)).toFixed(2)} MB
                          </p>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              setZipFile(null);
                              setZipSiteName('');
                            }}
                          >
                            <X className="h-4 w-4 mr-1" />
                            Supprimer
                          </Button>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <Upload className="h-12 w-12 mx-auto text-muted-foreground" />
                          <p className="font-medium">
                            Glissez-déposez votre ZIP ici
                          </p>
                          <p className="text-sm text-muted-foreground">
                            ou cliquez pour parcourir (max 50MB)
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Site Name */}
                    {zipFile && (
                      <>
                        <div className="space-y-2">
                          <Label>Nom du site</Label>
                          <Input
                            value={zipSiteName}
                            onChange={(e) => setZipSiteName(e.target.value)}
                            placeholder="mon-site"
                          />
                        </div>
                        
                        {/* Course Link */}
                        <div className="space-y-2">
                          <Label className="flex items-center gap-2">
                            <BookOpen className="h-4 w-4" />
                            Lier à un cours (optionnel)
                          </Label>
                          <select
                            value={zipCourseId}
                            onChange={(e) => setZipCourseId(e.target.value)}
                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                          >
                            <option value="">Aucun cours</option>
                            {courses.map((course) => (
                              <option key={course.id} value={course.id}>
                                {course.title}
                              </option>
                            ))}
                          </select>
                          <p className="text-xs text-muted-foreground">
                            Permet de partager ce site via le cours
                          </p>
                        </div>
                      </>
                    )}

                    {/* Progress */}
                    {deploying && activeTab === 'zip' && (
                      <div className="space-y-2">
                        <Progress value={uploadProgress} />
                        <p className="text-sm text-center text-muted-foreground">
                          {deployStatus}
                        </p>
                      </div>
                    )}

                    {/* Deploy Button */}
                    <Button
                      onClick={deployZip}
                      disabled={!zipFile || deploying}
                      className="w-full bg-purple-600 hover:bg-purple-700"
                    >
                      {deploying && activeTab === 'zip' ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          {deployStatus}
                        </>
                      ) : (
                        <>
                          <Rocket className="h-4 w-4 mr-2" />
                          Déployer le site
                        </>
                      )}
                    </Button>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Git Deploy Tab */}
              <TabsContent value="git" className="mt-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <GitBranch className="h-5 w-5" />
                      Déployer depuis Git
                    </CardTitle>
                    <CardDescription>
                      Clonez un dépôt GitHub, GitLab, Bitbucket ou auto-hébergé
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Git Connection & Repository Selector (if connections exist) */}
                    {gitConnections.length > 0 && (
                      <>
                        <div className="space-y-2">
                          <Label>Connexion Git</Label>
                          <Select
                            value={selectedConnection || ""}
                            onValueChange={(v) => handleConnectionSelect(v || null)}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Sélectionner une connexion (optionnel)" />
                            </SelectTrigger>
                            <SelectContent>
                              {gitConnections.map(conn => (
                                <SelectItem key={conn.id} value={conn.id}>
                                  <span className="flex items-center gap-2">
                                    <GitProviderIcon provider={conn.provider} className="h-4 w-4" />
                                    {conn.name} (@{conn.username})
                                  </span>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <p className="text-xs text-muted-foreground">
                            <a href="/admin" className="text-primary hover:underline">
                              Gérer les connexions dans Administration → Connexions Git
                            </a>
                          </p>
                        </div>

                        {/* Repository Selector (when connection is selected) */}
                        {selectedConnection && (
                          <div className="space-y-2">
                            <Label>Sélectionner un dépôt</Label>
                            <Popover open={repoSearchOpen} onOpenChange={(open) => {
                              setRepoSearchOpen(open);
                              // Load repos when popover opens if not already loaded
                              if (open && repos.length === 0 && !loadingRepos) {
                                loadRepos(selectedConnection);
                              }
                            }}>
                              <PopoverTrigger asChild>
                                <Button
                                  variant="outline"
                                  role="combobox"
                                  aria-expanded={repoSearchOpen}
                                  className="w-full justify-between"
                                >
                                  {selectedRepo ? (
                                    <span className="flex items-center gap-2 truncate">
                                      <GitBranch className="h-4 w-4 shrink-0" />
                                      {selectedRepo.full_name}
                                    </span>
                                  ) : (
                                    <span className="text-muted-foreground">Rechercher un dépôt...</span>
                                  )}
                                  <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-[400px] p-0" align="start">
                                <Command>
                                  <CommandInput
                                    placeholder="Rechercher un dépôt..."
                                    value={repoSearch}
                                    onValueChange={(v) => {
                                      setRepoSearch(v);
                                      // Search with any input or load all if empty
                                      loadRepos(selectedConnection, v);
                                    }}
                                  />
                                  <CommandList>
                                    {loadingRepos ? (
                                      <div className="py-6 text-center">
                                        <Loader2 className="h-4 w-4 animate-spin mx-auto" />
                                      </div>
                                    ) : repos.length === 0 ? (
                                      <CommandEmpty>Aucun dépôt trouvé</CommandEmpty>
                                    ) : (
                                      <CommandGroup>
                                        {repos.map((repo) => (
                                          <CommandItem
                                            key={repo.id}
                                            value={repo.full_name}
                                            onSelect={() => handleRepoSelect(repo)}
                                            className="flex items-center gap-2 cursor-pointer"
                                          >
                                            <GitBranch className="h-4 w-4 shrink-0" />
                                            <div className="flex-1 truncate">
                                              <p className="font-medium truncate">{repo.full_name}</p>
                                              <p className="text-xs text-muted-foreground truncate">
                                                {repo.description || 'Pas de description'}
                                              </p>
                                            </div>
                                            <div className="shrink-0 text-right text-xs text-muted-foreground">
                                              {repo.private && <Lock className="h-3 w-3 inline mr-1" />}
                                              {repo.language && <span className="mr-2">{repo.language}</span>}
                                              {repo.last_activity && (
                                                <span>{new Date(repo.last_activity).toLocaleDateString('fr-FR')}</span>
                                              )}
                                            </div>
                                          </CommandItem>
                                        ))}
                                      </CommandGroup>
                                    )}
                                  </CommandList>
                                </Command>
                              </PopoverContent>
                            </Popover>
                          </div>
                        )}
                      </>
                    )}

                    {/* Repository URL - Always visible (optional if connection selected) */}
                    <div className="space-y-2">
                      <Label>URL du dépôt {!selectedRepo && '*'}</Label>
                      <div className="relative">
                        <Input
                          value={gitForm.repoUrl}
                          onChange={(e) => setGitForm({...gitForm, repoUrl: e.target.value})}
                          placeholder="https://github.com/user/repo.git"
                          className="pl-10"
                        />
                        <div className="absolute left-3 top-1/2 -translate-y-1/2">
                          {gitProvider ? (
                            <GitProviderIcon provider={gitProvider} className="h-4 w-4 text-muted-foreground" />
                          ) : (
                            <Link2 className="h-4 w-4 text-muted-foreground" />
                          )}
                        </div>
                      </div>
                      {selectedRepo && (
                        <p className="text-xs text-muted-foreground">
                          URL auto-remplie depuis le dépôt sélectionné
                        </p>
                      )}
                    </div>

                    {/* Branch & Subfolder */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Branche</Label>
                        {branches.length > 0 ? (
                          <Select
                            value={gitForm.branch}
                            onValueChange={(v) => setGitForm({...gitForm, branch: v})}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {branches.map(b => (
                                <SelectItem key={b.name} value={b.name}>
                                  {b.name} {b.is_default && '(défaut)'}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <Input
                            value={gitForm.branch}
                            onChange={(e) => setGitForm({...gitForm, branch: e.target.value})}
                            placeholder="main"
                          />
                        )}
                        {loadingBranches && (
                          <p className="text-xs text-muted-foreground flex items-center gap-1">
                            <Loader2 className="h-3 w-3 animate-spin" /> Chargement des branches...
                          </p>
                        )}
                      </div>
                      <div className="space-y-2">
                        <Label>Sous-dossier (optionnel)</Label>
                        <Input
                          value={gitForm.subfolder}
                          onChange={(e) => setGitForm({...gitForm, subfolder: e.target.value})}
                          placeholder="docs/"
                        />
                      </div>
                    </div>

                    {/* Site Name */}
                    <div className="space-y-2">
                      <Label>Nom du site (optionnel)</Label>
                      <Input
                        value={gitForm.siteName}
                        onChange={(e) => setGitForm({...gitForm, siteName: e.target.value})}
                        placeholder="Auto-détecté depuis le dépôt"
                      />
                    </div>

                    {/* Course Link */}
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <BookOpen className="h-4 w-4" />
                        Lier à un cours (optionnel)
                      </Label>
                      <select
                        value={gitForm.courseId}
                        onChange={(e) => setGitForm({...gitForm, courseId: e.target.value})}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      >
                        <option value="">Aucun cours</option>
                        {courses.map((course) => (
                          <option key={course.id} value={course.id}>
                            {course.title}
                          </option>
                        ))}
                      </select>
                      <p className="text-xs text-muted-foreground">
                        Permet de partager ce site via le cours
                      </p>
                    </div>

                    {/* Private Repository Section */}
                    <Collapsible open={gitForm.isPrivate} onOpenChange={(open) => setGitForm({...gitForm, isPrivate: open})}>
                      <CollapsibleTrigger asChild>
                        <Button variant="ghost" className="w-full justify-between">
                          <span className="flex items-center gap-2">
                            <Lock className="h-4 w-4" />
                            Dépôt privé ?
                          </span>
                          <ChevronDown className={`h-4 w-4 transition-transform ${gitForm.isPrivate ? 'rotate-180' : ''}`} />
                        </Button>
                      </CollapsibleTrigger>
                      <CollapsibleContent className="space-y-4 pt-4">
                        <div className="space-y-2">
                          <Label>Nom d'utilisateur</Label>
                          <Input
                            value={gitForm.username}
                            onChange={(e) => setGitForm({...gitForm, username: e.target.value})}
                            placeholder="username"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Token d'accès personnel</Label>
                          <div className="relative">
                            <Input
                              type={showToken ? 'text' : 'password'}
                              value={gitForm.token}
                              onChange={(e) => setGitForm({...gitForm, token: e.target.value})}
                              placeholder="ghp_xxxx..."
                              className="pr-10"
                            />
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                              onClick={() => setShowToken(!showToken)}
                            >
                              {showToken ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </Button>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            <a href="https://github.com/settings/tokens" target="_blank" rel="noopener noreferrer" className="text-purple-500 hover:underline">
                              Comment générer un token sur GitHub
                            </a>
                          </p>
                        </div>
                      </CollapsibleContent>
                    </Collapsible>

                    {/* Status */}
                    {deploying && activeTab === 'git' && (
                      <div className="flex items-center gap-2 p-3 rounded-lg bg-muted">
                        <Loader2 className="h-4 w-4 animate-spin text-purple-500" />
                        <span className="text-sm">{deployStatus}</span>
                      </div>
                    )}

                    {/* Deploy Button */}
                    <Button
                      onClick={deployGit}
                      disabled={!gitForm.repoUrl || deploying}
                      className="w-full bg-red-600 hover:bg-red-700"
                    >
                      {deploying && activeTab === 'git' ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          {deployStatus}
                        </>
                      ) : (
                        <>
                          <Rocket className="h-4 w-4 mr-2" />
                          Clone & Deploy
                        </>
                      )}
                    </Button>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Dockerfile Deploy Tab */}
              <TabsContent value="dockerfile" className="mt-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Container className="h-5 w-5" />
                      Déployer via Dockerfile
                    </CardTitle>
                    <CardDescription>
                      Clonez un dépôt et buildez avec un Dockerfile
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Git Connection */}
                    {gitConnections.length > 0 && (
                      <>
                        <div className="space-y-2">
                          <Label>Connexion Git</Label>
                          <Select
                            value={dockerSelectedConnection || ""}
                            onValueChange={(v) => handleDockerConnectionSelect(v || null)}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Sélectionner une connexion" />
                            </SelectTrigger>
                            <SelectContent>
                              {gitConnections.map(conn => (
                                <SelectItem key={conn.id} value={conn.id}>
                                  <span className="flex items-center gap-2">
                                    <GitProviderIcon provider={conn.provider} className="h-4 w-4" />
                                    {conn.name} (@{conn.username})
                                  </span>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Repo Selector */}
                        {dockerSelectedConnection && (
                          <div className="space-y-2">
                            <Label>Sélectionner un dépôt</Label>
                            <Popover open={dockerRepoSearchOpen} onOpenChange={setDockerRepoSearchOpen}>
                              <PopoverTrigger asChild>
                                <Button variant="outline" className="w-full justify-between">
                                  {dockerSelectedRepo ? (
                                    <span className="flex items-center gap-2">
                                      <GitBranch className="h-4 w-4" />
                                      {dockerSelectedRepo.full_name}
                                    </span>
                                  ) : (
                                    <span className="flex items-center gap-2 text-muted-foreground">
                                      <Search className="h-4 w-4" />
                                      Rechercher un dépôt...
                                    </span>
                                  )}
                                  <ChevronDown className="h-4 w-4" />
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-full p-0" align="start">
                                <Command>
                                  <CommandInput
                                    placeholder="Rechercher..."
                                    onValueChange={(search) => {
                                      if (search.length >= 2) loadDockerRepos(dockerSelectedConnection, search);
                                    }}
                                  />
                                  <CommandList>
                                    <CommandEmpty>
                                      {dockerLoadingRepos ? (
                                        <span className="flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Chargement...</span>
                                      ) : 'Aucun dépôt trouvé'}
                                    </CommandEmpty>
                                    <CommandGroup>
                                      {dockerRepos.map(repo => (
                                        <CommandItem key={repo.id} onSelect={() => handleDockerRepoSelect(repo)}>
                                          <GitBranch className="h-4 w-4 mr-2" />
                                          {repo.full_name}
                                          {repo.private && <Lock className="h-3 w-3 ml-auto" />}
                                        </CommandItem>
                                      ))}
                                    </CommandGroup>
                                  </CommandList>
                                </Command>
                              </PopoverContent>
                            </Popover>
                          </div>
                        )}
                      </>
                    )}

                    {/* Repo URL */}
                    <div className="space-y-2">
                      <Label>URL du dépôt</Label>
                      <Input
                        value={dockerForm.repoUrl}
                        onChange={(e) => setDockerForm({...dockerForm, repoUrl: e.target.value})}
                        placeholder="https://github.com/user/repo.git"
                        disabled={!!dockerSelectedRepo}
                      />
                    </div>

                    {/* Branch & Dockerfile */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Branche</Label>
                        {dockerBranches.length > 0 ? (
                          <Select
                            value={dockerForm.branch}
                            onValueChange={(v) => setDockerForm({...dockerForm, branch: v})}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {dockerBranches.map(b => (
                                <SelectItem key={b.name} value={b.name}>
                                  {b.name} {b.is_default && '(default)'}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <Input
                            value={dockerForm.branch}
                            onChange={(e) => setDockerForm({...dockerForm, branch: e.target.value})}
                            placeholder="main"
                          />
                        )}
                      </div>
                      <div className="space-y-2">
                        <Label className="flex items-center gap-1">
                          <FileCode className="h-4 w-4 text-orange-500" />
                          Dockerfile *
                        </Label>
                        <Input
                          value={dockerForm.dockerfile}
                          onChange={(e) => setDockerForm({...dockerForm, dockerfile: e.target.value})}
                          placeholder="Dockerfile"
                        />
                      </div>
                    </div>

                    {/* Site Name */}
                    <div className="space-y-2">
                      <Label>Nom du site (optionnel)</Label>
                      <Input
                        value={dockerForm.siteName}
                        onChange={(e) => setDockerForm({...dockerForm, siteName: e.target.value})}
                        placeholder="Auto-détecté depuis le dépôt"
                      />
                    </div>

                    {/* Course Link */}
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <BookOpen className="h-4 w-4" />
                        Lier à un cours (optionnel)
                      </Label>
                      <select
                        value={dockerForm.courseId}
                        onChange={(e) => setDockerForm({...dockerForm, courseId: e.target.value})}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      >
                        <option value="">Aucun cours</option>
                        {courses.map((course) => (
                          <option key={course.id} value={course.id}>
                            {course.title}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Status */}
                    {deploying && activeTab === 'dockerfile' && (
                      <div className="flex items-center gap-2 p-3 rounded-lg bg-muted">
                        <Loader2 className="h-4 w-4 animate-spin text-orange-500" />
                        <span className="text-sm">{deployStatus}</span>
                      </div>
                    )}

                    {/* Deploy Button */}
                    <Button
                      onClick={deployDockerfile}
                      disabled={!dockerForm.repoUrl || !dockerForm.dockerfile || deploying}
                      className="w-full bg-orange-600 hover:bg-orange-700"
                    >
                      {deploying && activeTab === 'dockerfile' ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          {deployStatus}
                        </>
                      ) : (
                        <>
                          <Container className="h-4 w-4 mr-2" />
                          Build & Deploy
                        </>
                      )}
                    </Button>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>

          {/* Sites List / Preview */}
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <FolderOpen className="h-5 w-5" />
                    Sites déployés
                  </span>
                  <Badge variant="secondary">{sites.length}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                  </div>
                ) : sites.length === 0 ? (
                  <div className="text-center py-8">
                    <Globe className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                    <p className="text-muted-foreground">
                      Aucun site déployé
                    </p>
                    <p className="text-sm text-muted-foreground/70">
                      Uploadez un ZIP ou connectez un dépôt Git !
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3 max-h-[500px] overflow-y-auto pr-2">
                    {sites.map((site) => {
                      const isSiteHidden = site.is_visible === false;
                      return (
                      <div
                        key={site.id}
                        className={`
                          p-4 rounded-lg border transition-all cursor-pointer
                          ${previewSite?.id === site.id 
                            ? 'border-purple-500 bg-purple-500/5' 
                            : isSiteHidden
                              ? 'border-yellow-300 bg-yellow-50/30 dark:bg-yellow-900/10 opacity-60'
                              : 'border-border hover:border-purple-500/50'
                          }
                        `}
                        onClick={() => setPreviewSite(site)}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-medium truncate">{site.name}</h4>
                              {site.source === 'zip' ? (
                                <Badge variant="secondary" className="text-xs">
                                  <Package className="h-3 w-3 mr-1" />
                                  ZIP
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="text-xs">
                                  <GitProviderIcon provider={site.provider} className="h-3 w-3 mr-1" />
                                  {site.branch || 'main'}
                                </Badge>
                              )}
                              <Badge 
                                className={`text-xs ${
                                  site.status === 'live' 
                                    ? 'bg-green-500' 
                                    : site.status === 'error' 
                                      ? 'bg-red-500' 
                                      : 'bg-yellow-500'
                                }`}
                              >
                                {site.status === 'live' ? '✓ Live' : site.status}
                              </Badge>
                              {isSiteHidden && (
                                <Badge variant="outline" className="text-xs text-yellow-600 border-yellow-300 bg-yellow-100">
                                  Caché
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-3 text-xs text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <FileText className="h-3 w-3" />
                                {site.pages?.length || 0} pages
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {formatDate(site.deployedAt)}
                              </span>
                              {site.courseId && (
                                <span className="flex items-center gap-1 text-purple-500">
                                  <BookOpen className="h-3 w-3" />
                                  Lié à un cours
                                </span>
                              )}
                            </div>
                            {site.source === 'git' && site.lastCommitSha && (
                              <p className="text-xs text-muted-foreground mt-1 truncate">
                                <code className="bg-muted px-1 rounded">{site.lastCommitSha}</code>
                                {' '}{site.lastCommitMessage}
                              </p>
                            )}
                          </div>
                          <div className="flex gap-1 shrink-0">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={(e) => { e.stopPropagation(); openSite(site); }}
                              title="Ouvrir"
                            >
                              <ExternalLink className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={(e) => { e.stopPropagation(); openEditModal(site); }}
                              title="Modifier"
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={(e) => { e.stopPropagation(); copyUrl(site); }}
                              title="Copier URL"
                            >
                              <Copy className="h-4 w-4" />
                            </Button>
                            {site.source === 'git' && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={(e) => { e.stopPropagation(); redeploySite(site); }}
                                title="Redéployer"
                              >
                                <RefreshCw className="h-4 w-4" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={async (e) => {
                                e.stopPropagation();
                                try {
                                  const resp = await fetch(`${API_URL}/api/sites/${site.id}/visibility`, {
                                    method: 'PATCH',
                                    headers: { 'Authorization': `Bearer ${token}` }
                                  });
                                  if (resp.ok) {
                                    toast.success(isSiteHidden ? 'Site visible' : 'Site masqué');
                                    loadSites();
                                  }
                                } catch (err) {
                                  toast.error('Erreur');
                                }
                              }}
                              title={isSiteHidden ? 'Rendre visible' : 'Masquer'}
                            >
                              {isSiteHidden ? (
                                <EyeOff className="h-4 w-4 text-yellow-600" />
                              ) : (
                                <Eye className="h-4 w-4 text-green-600" />
                              )}
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-destructive hover:text-destructive"
                              onClick={(e) => { e.stopPropagation(); setDeleteConfirm(site); }}
                              title="Supprimer"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Preview Panel */}
            {previewSite && (
              <Card>
                <CardHeader className="py-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <Eye className="h-4 w-4" />
                      Aperçu: {previewSite.name}
                    </CardTitle>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => setIsFullscreen(!isFullscreen)}
                      >
                        {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => openSite(previewSite)}
                      >
                        <ExternalLink className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => setPreviewSite(null)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="border-t">
                    <iframe
                      src={`${API_URL}/s/${previewSite.id}/`}
                      className={`w-full border-0 ${isFullscreen ? 'h-[80vh]' : 'h-[400px]'}`}
                      title={previewSite.name}
                    />
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Success Modal */}
      <Dialog open={!!successModal} onOpenChange={() => setSuccessModal(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="h-6 w-6 text-green-500" />
              Site déployé avec succès !
            </DialogTitle>
          </DialogHeader>
          {successModal && (
            <div className="space-y-4">
              <div className="p-3 rounded-lg bg-muted">
                <p className="text-sm text-muted-foreground mb-1">
                  {successModal.source === 'git' 
                    ? `Déployé depuis ${successModal.provider}: ${successModal.repoUrl} (branche: ${successModal.branch})`
                    : `Déployé depuis: ${successModal.zipFilename}`
                  }
                </p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-sm bg-background p-2 rounded truncate">
                    {API_URL}/s/{successModal.siteId}/
                  </code>
                  <Button
                    variant="outline"
                    size="icon"
                    className="shrink-0"
                    onClick={() => {
                      navigator.clipboard.writeText(`${API_URL}/s/${successModal.siteId}/`);
                      toast.success('URL copiée !');
                    }}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <div>
                <p className="text-sm font-medium mb-2">Pages détectées ({successModal.pages?.length || 0}):</p>
                <div className="max-h-32 overflow-y-auto space-y-1">
                  {successModal.pages?.slice(0, 10).map((page, idx) => (
                    <a
                      key={idx}
                      href={`${API_URL}/s/${successModal.siteId}/${page}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-sm text-purple-500 hover:underline"
                    >
                      <FileCode className="h-3 w-3" />
                      {page}
                    </a>
                  ))}
                  {(successModal.pages?.length || 0) > 10 && (
                    <p className="text-xs text-muted-foreground">
                      ... et {successModal.pages.length - 10} autres fichiers
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSuccessModal(null)}>
              Fermer
            </Button>
            <Button onClick={() => {
              if (successModal) openSite(successModal);
              setSuccessModal(null);
            }}>
              <ExternalLink className="h-4 w-4 mr-2" />
              Ouvrir le site
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-5 w-5" />
              Supprimer le site ?
            </DialogTitle>
          </DialogHeader>
          {deleteConfirm && (
            <p className="text-muted-foreground">
              Êtes-vous sûr de vouloir supprimer <strong>{deleteConfirm.name}</strong> ?
              Cette action est irréversible.
            </p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>
              Annuler
            </Button>
            <Button variant="destructive" onClick={() => deleteSite(deleteConfirm)}>
              <Trash2 className="h-4 w-4 mr-2" />
              Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Site Modal */}
      <Dialog open={!!editModal} onOpenChange={() => setEditModal(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Modifier le site
            </DialogTitle>
          </DialogHeader>
          {editModal && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Nom du site</Label>
                <Input
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  placeholder="Mon site"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <BookOpen className="h-4 w-4" />
                  Lier à un cours
                </Label>
                <select
                  value={editForm.courseId}
                  onChange={(e) => setEditForm({ ...editForm, courseId: e.target.value })}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <option value="">Aucun cours</option>
                  {courses.map((course) => (
                    <option key={course.id} value={course.id}>
                      {course.title}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-muted-foreground">
                  Le site sera affiché sur la page du cours sélectionné
                </p>
              </div>

              <div className="p-3 rounded-lg bg-muted text-xs text-muted-foreground">
                <p><strong>Source:</strong> {editModal.source === 'git' ? `Git (${editModal.provider})` : 'ZIP'}</p>
                {editModal.repoUrl && <p><strong>Dépôt:</strong> {editModal.repoUrl}</p>}
                <p><strong>Déployé le:</strong> {formatDate(editModal.deployedAt)}</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditModal(null)}>
              Annuler
            </Button>
            <Button onClick={saveEditSite} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Enregistrement...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Enregistrer
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </WorkspaceLayout>
  );
}
